import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { IClient } from "../Client.model";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { requests } from "../../../../services/http-common";
import { phoneRegex } from "../../../../constants/regex";

type TClientForm = Omit<IClient, "clientId">;

export const useClientForm = (clientId?: string) => {
  const formSchema = Yup.object().shape({
    title: Yup.string().nullable(true),
    name: Yup.string().nullable(true),
    surname: Yup.string().nullable(true),
    companyName: Yup.string().nullable(true),
    contactName: Yup.string().nullable(true),
    // displayNameAs: Yup.string().nullable(true),
    country: Yup.string(),
    state: Yup.string(),
    city: Yup.string(),
    ZIP: Yup.string(),
    email: Yup.string().nullable(true),
    phone: Yup.string().matches(phoneRegex, 'Invalid phone number').nullable(true),
    mobile: Yup.string().matches(phoneRegex, 'Invalid phone number').nullable(true),
    fax: Yup.string().nullable(true),
    website: Yup.string().test({
      name: 'notEmptyOrInvalidUrl',
      test: (value) => {
        if (!value) {
          return true;
        }
    
        const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}\/?[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*$/;
        return urlPattern.test(value);
      },
      message: 'Invalid URL',
    }).nullable(true),    
    address: Yup.string().nullable(true),
  });

  const hookForm = useForm<TClientForm>({
    resolver: yupResolver(formSchema),
    shouldFocusError: false,
    defaultValues: {
      title: "",
      name: "",
      surname: "",
      companyName: "",
      contactName: "",
      // displayNameAs: "",
      email: "",
      phone: "",
      mobile: "",
      fax: "",
      website: "",
      address: "",
    },
  });

  const { reset, watch } = hookForm;

  const [state] = watch(['state'])

  const { data: cities } = useQuery({
    queryKey: ['cities', state],
    enabled: !!state,
    queryFn: () => {
      return requests.get(`/administrative-division/usa/cities/${state}`)
    },
    select: (data: { name: string }[]) => {
      return data.map(s => ({ label: s.name, value: s.name }))
    }
  })

  const { refetch } = useQuery({
    queryKey: [`client-form-${clientId}`],
    queryFn: () => {
      return requests.get(`/client/${clientId}`);
    },
    enabled: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    onSuccess: ({
      title,
      name,
      surname,
      companyName,
      contactName,
      // displayNameAs,
      email,
      phone,
      mobile,
      fax,
      website,
      address,
      country,
      city,
      state,
      ZIP,
      customerCategory,
    }) => {
      reset({
        title: title || "",
        name: name || "",
        surname: surname || "",
        companyName: companyName || "",
        contactName: contactName || "",
        // displayNameAs: displayNameAs || "",
        email: email || "",
        phone: phone || "",
        mobile: mobile || "",
        fax: fax || "",
        website: website || "",
        address: address || "",
        country: country || "",
        state: state || "",
        city: city || "",
        ZIP: ZIP || "",
        customerCategory,
      });
    },
  });

  useEffect(() => {
    if (clientId) {
      refetch();
    }
  }, [clientId, refetch]);

  return {
    hookForm,
    cities
  };
};
